import React from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";

import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { validate as uuidValidate } from "uuid";

import {
	AWARDS,
	AWARDS_BASE_ROUTE, PROJECTS,
	PROJECTS_BASE_ROUTE,
	PROPOSALS,
	PROPOSALS_BASE_ROUTE,
	SUBAWARDS,
	SUBAWARDS_BASE_ROUTE,
} from "globals/constants";
import { getAward } from "store/actions/award";
import { getProject } from "store/actions/project";
import { getProposal } from "store/actions/proposal";
import { getSubaward } from "store/actions/subaward";
import { awardSelectors, projectSelectors, proposalSelectors, subawardSelectors } from "store/selectors";
import { spUrl } from "utils";

export const useRecord = () => {
	const { taskId, awardId, subawardId, proposalId, projectId } = useParams();
	const history = useHistory();

	const award = useSelector(awardSelectors.award);
	const subaward = useSelector(subawardSelectors.subaward);
	const proposal = useSelector(proposalSelectors.proposal);
	const project = useSelector(projectSelectors.project);

	const isAward = isNonEmptyString(awardId);
	const isSubAward = isNonEmptyString(subawardId);
	const isSubAwardLinkedToAward = isSubAward && subaward.awardId;
	const isProposal = isNonEmptyString(proposalId);
	const isProject = isNonEmptyString(projectId);

	React.useEffect(() => {
		if (isProposal) {
			if (uuidValidate(proposalId)) {
				getProposal(proposalId);
			} else {
				history.push("/sp/proposals");
			}
		} else if (isSubAward) {
			if (uuidValidate(subawardId)) {
				getSubaward(subawardId);
			} else {
				history.push(`/sp/awards/${awardId}`);
			}
		} else if (isAward) {
			if (uuidValidate(awardId)) {
				getAward(awardId);
			} else {
				history.push("/sp/awards");
			}
		} else if (isProject) {
			if (uuidValidate(projectId)) {
				getProject(projectId);
			} else {
				history.push("/sp/projects");
			}
		}
	}, [ awardId, history, isAward, isProposal, isSubAward, proposalId, subawardId, isProject, projectId ]);


	/**
	 * @returns {unknown} - The selected argument based on the record type.
	 */
	const getArgumentByRecordType = (...args) => {
		if (isSubAwardLinkedToAward && args.length >= 5) {
			return args[4];
		}

		return isSubAward ? args[0] : isAward ? args[1] : isProposal ? args[2] : args[3];
	};

	const record = getArgumentByRecordType(subaward, award, proposal, project);
	const recordUrl = getArgumentByRecordType(
		spUrl(`${SUBAWARDS_BASE_ROUTE}/${subawardId}`),
		spUrl(`${AWARDS_BASE_ROUTE}/${awardId}`),
		spUrl(`${PROPOSALS_BASE_ROUTE}/${proposalId}`),
		spUrl(`${PROJECTS_BASE_ROUTE}/${projectId}`),
		spUrl(`${AWARDS_BASE_ROUTE}/${subaward.awardId}/sub-awards/${subawardId}`)
	);
	const recordNumber = getArgumentByRecordType(
		record.subawardNumber?.split("-").pop(),
		record.awardNumber,
		record.proposalNumber,
		record.projectNumber,
    record.subawardNumber?.split("-").pop(),
	);
	const recordId = getArgumentByRecordType(subawardId, awardId, proposalId, projectId);

	const recordBreadcrumbs = isSubAwardLinkedToAward
		? [
			{ text: record.awardNumber, link: spUrl(`${AWARDS_BASE_ROUTE}/${record.awardId}`) },
			{ text: recordNumber, link: recordUrl }
		]
		: [ { text: recordNumber, link: recordUrl } ];

	const submissionType = isSubAward ? SUBAWARDS :
		isProposal ? PROPOSALS
			: isAward ? AWARDS
				: isProject ? PROJECTS
					: null;

	return {
		taskId,
		recordBreadcrumbs,
		record,
		recordId,
		recordUrl,
		submissionType,
	};
};
